<script setup lang="ts">
import {
  computed,
  defineComponent,
  h,
  normalizeClass,
  onMounted,
  useAttrs,
  useId,
  useTemplateRef,
  type Component,
  type StyleValue,
  type VNode
} from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import UCheckbox from "@nuxt/ui/components/Checkbox.vue"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faCheck, faMinus } from "@fortawesome/free-solid-svg-icons"
import type { CheckboxColor, CheckboxSize, CheckboxIcon, CheckboxIndicator } from "./checkbox-types"

const props = withDefaults(
  defineProps<{
    /** Element or component used for the outer wrapper. */
    as?: string | Component
    label?: string
    description?: string
    color?: CheckboxColor
    variant?: "list" | "card"
    size?: CheckboxSize
    indicator?: CheckboxIndicator
    indeterminate?: boolean
    /** Highlight the checkbox border in the selected color. */
    highlight?: boolean
    /** FontAwesome icon displayed when checked. Defaults to a check mark. */
    icon?: CheckboxIcon
    disabled?: boolean
    required?: boolean
    name?: string
    value?: string | number
    id?: string
    autofocus?: boolean
  }>(),
  {
    as: "div",
    color: "secondary",
    variant: "list",
    size: "md",
    indicator: "start",
    indeterminate: false,
    highlight: false,
    disabled: false,
    required: false,
    value: "on",
    autofocus: false
  }
)

defineOptions({ inheritAttrs: false })
const slots = defineSlots<{
  label?(props: { label: string | undefined }): VNode[]
  description?(props: { description: string | undefined }): VNode[]
}>()
const modelValue = defineModel<boolean>({ required: true, default: false })
const emit = defineEmits<{
  change: [event: Event]
}>()

const attrs = useAttrs()
const checkbox = useTemplateRef<{ $el: HTMLElement }>("checkbox")
function control() {
  return checkbox.value?.$el.querySelector<HTMLButtonElement>('[role="checkbox"]')
}
defineExpose({ focus: () => control()?.focus() })
const generatedId = useId()
const inputId = computed(() => props.id ?? generatedId)
const descriptionId = computed(() => `${inputId.value}-description`)
const hasDescription = computed(() => !!props.description || !!slots.description)

// Keep native attributes and listeners on the input; layout classes belong to the wrapper.
function inputAttrs() {
  const forwarded = { ...attrs }
  delete forwarded.class
  delete forwarded.style
  return forwarded
}

function describedBy() {
  return (
    [hasDescription.value ? descriptionId.value : undefined, attrs["aria-describedby"]]
      .filter(Boolean)
      .join(" ") || undefined
  )
}

const sizes = {
  xs: { control: "size-4", text: "text-xs", card: "p-1.5" },
  sm: { control: "size-4.5", text: "text-xs", card: "p-1.5" },
  md: { control: "size-5", text: "text-sm", card: "p-1.5" },
  lg: { control: "size-5.5", text: "text-sm", card: "p-1.5" },
  xl: { control: "size-6", text: "text-base", card: "p-1.5" }
} satisfies Record<CheckboxSize, { control: string; text: string; card: string }>
const sizeClasses = computed(() => sizes[props.size])
const appearances = {
  "gray-lighten-1": {
    checked: "bg-gray-lighten-1! dark:bg-gray-lighten-1-dark!",
    border: "border-gray-lighten-1 dark:border-gray-lighten-1-dark",
    icon: "text-on-surface dark:text-on-surface-dark",
    focus: "focus-visible:outline-gray-lighten-1 dark:focus-visible:outline-gray-lighten-1-dark",
    ring: "ring-gray-lighten-1 dark:ring-gray-lighten-1-dark",
    card: "bg-gray-lighten-1 dark:bg-gray-lighten-1-dark",
    hover: "hover:bg-gray-lighten-1 dark:hover:bg-gray-lighten-1-dark",
    cardFocus:
      "has-focus-visible:border-gray-lighten-1 dark:has-focus-visible:border-gray-lighten-1-dark"
  },
  "gray-lighten-2": {
    checked: "bg-gray-lighten-2! dark:bg-gray-lighten-2-dark!",
    border: "border-gray-lighten-2 dark:border-gray-lighten-2-dark",
    icon: "text-on-surface dark:text-on-surface-dark",
    focus: "focus-visible:outline-gray-lighten-2 dark:focus-visible:outline-gray-lighten-2-dark",
    ring: "ring-gray-lighten-2 dark:ring-gray-lighten-2-dark",
    card: "bg-gray-lighten-2 dark:bg-gray-lighten-2-dark",
    hover: "hover:bg-gray-lighten-2 dark:hover:bg-gray-lighten-2-dark",
    cardFocus:
      "has-focus-visible:border-gray-lighten-2 dark:has-focus-visible:border-gray-lighten-2-dark"
  },
  primary: {
    checked: "bg-primary! dark:bg-primary-dark!",
    border: "border-primary dark:border-primary-dark",
    icon: "text-on-primary dark:text-on-primary-dark",
    focus: "focus-visible:outline-primary dark:focus-visible:outline-primary-dark",
    ring: "ring-primary dark:ring-primary-dark",
    card: "bg-primary/10 dark:bg-primary-dark/10",
    hover: "hover:bg-primary/5 dark:hover:bg-primary-dark/5",
    cardFocus: "has-focus-visible:border-primary dark:has-focus-visible:border-primary-dark"
  },
  "primary-lighten-1": {
    checked: "bg-primary-lighten-1! dark:bg-primary-lighten-1-dark!",
    border: "border-primary-lighten-1 dark:border-primary-lighten-1-dark",
    icon: "text-on-primary-lighten-1 dark:text-on-primary-lighten-1-dark",
    focus:
      "focus-visible:outline-primary-lighten-1 dark:focus-visible:outline-primary-lighten-1-dark",
    ring: "ring-primary-lighten-1 dark:ring-primary-lighten-1-dark",
    card: "bg-primary-lighten-1/10 dark:bg-primary-lighten-1-dark/10",
    hover: "hover:bg-primary-lighten-1/5 dark:hover:bg-primary-lighten-1-dark/5",
    cardFocus:
      "has-focus-visible:border-primary-lighten-1 dark:has-focus-visible:border-primary-lighten-1-dark"
  },
  secondary: {
    checked: "bg-secondary! dark:bg-secondary-dark!",
    border: "border-secondary dark:border-secondary-dark",
    icon: "text-on-secondary dark:text-on-secondary-dark",
    focus: "focus-visible:outline-secondary dark:focus-visible:outline-secondary-dark",
    ring: "ring-secondary dark:ring-secondary-dark",
    card: "bg-secondary/10 dark:bg-secondary-dark/10",
    hover: "hover:bg-secondary/5 dark:hover:bg-secondary-dark/5",
    cardFocus: "has-focus-visible:border-secondary dark:has-focus-visible:border-secondary-dark"
  },
  surface: {
    checked: "bg-surface! dark:bg-surface-dark!",
    border: "border-surface dark:border-surface-dark",
    icon: "text-on-surface dark:text-on-surface-dark",
    focus: "focus-visible:outline-surface dark:focus-visible:outline-surface-dark",
    ring: "ring-surface dark:ring-surface-dark",
    card: "bg-surface/10 dark:bg-surface-dark/10",
    hover: "hover:bg-surface/5 dark:hover:bg-surface-dark/5",
    cardFocus: "has-focus-visible:border-surface dark:has-focus-visible:border-surface-dark"
  },
  info: {
    checked: "bg-info! dark:bg-info-dark!",
    border: "border-info dark:border-info-dark",
    icon: "text-on-info dark:text-on-info-dark",
    focus: "focus-visible:outline-info dark:focus-visible:outline-info-dark",
    ring: "ring-info dark:ring-info-dark",
    card: "bg-info/10 dark:bg-info-dark/10",
    hover: "hover:bg-info/5 dark:hover:bg-info-dark/5",
    cardFocus: "has-focus-visible:border-info dark:has-focus-visible:border-info-dark"
  },
  warning: {
    checked: "bg-warning! dark:bg-warning-dark!",
    border: "border-warning dark:border-warning-dark",
    icon: "text-on-warning dark:text-on-warning-dark",
    focus: "focus-visible:outline-warning dark:focus-visible:outline-warning-dark",
    ring: "ring-warning dark:ring-warning-dark",
    card: "bg-warning/10 dark:bg-warning-dark/10",
    hover: "hover:bg-warning/5 dark:hover:bg-warning-dark/5",
    cardFocus: "has-focus-visible:border-warning dark:has-focus-visible:border-warning-dark"
  },
  error: {
    checked: "bg-error! dark:bg-error-dark!",
    border: "border-error dark:border-error-dark",
    icon: "text-on-error dark:text-on-error-dark",
    focus: "focus-visible:outline-error dark:focus-visible:outline-error-dark",
    ring: "ring-error dark:ring-error-dark",
    card: "bg-error/10 dark:bg-error-dark/10",
    hover: "hover:bg-error/5 dark:hover:bg-error-dark/5",
    cardFocus: "has-focus-visible:border-error dark:has-focus-visible:border-error-dark"
  },
  success: {
    checked: "bg-success! dark:bg-success-dark!",
    border: "border-success dark:border-success-dark",
    icon: "text-on-success dark:text-on-success-dark",
    focus: "focus-visible:outline-success dark:focus-visible:outline-success-dark",
    ring: "ring-success dark:ring-success-dark",
    card: "bg-success/10 dark:bg-success-dark/10",
    hover: "hover:bg-success/5 dark:hover:bg-success-dark/5",
    cardFocus: "has-focus-visible:border-success dark:has-focus-visible:border-success-dark"
  }
} as const satisfies Record<
  CheckboxColor,
  {
    checked: string
    border: string
    icon: string
    focus: string
    ring: string
    card: string
    hover: string
    cardFocus: string
  }
>
const appearance = computed(() => appearances[props.color])
const checkboxIcon = computed<IconProp>(() =>
  props.indeterminate ? faMinus : ((props.icon as IconProp) ?? faCheck)
)

// Nuxt's icon prop also accepts a component; keep the public FontAwesome API.
const CheckIcon = defineComponent({
  setup: () => () => h(FontAwesomeIcon, { icon: checkboxIcon.value, "aria-hidden": true })
})

function onUpdate(value: boolean | "indeterminate") {
  if (props.disabled || value === "indeterminate") return
  modelValue.value = value
  const event = new CustomEvent("change", { bubbles: true, detail: value })
  // Dispatch from the interactive control so change handlers retain a DOM target.
  control()?.dispatchEvent(event)
  emit("change", event)
}

function onEnter(event: KeyboardEvent) {
  if (!props.disabled && !event.repeat) control()?.click()
}

function onCardClick(event: MouseEvent) {
  if (props.variant !== "card" || props.disabled || props.as === "label" || event.defaultPrevented)
    return
  // Labels already activate the input; nested interactive content keeps its own action.
  if (
    (event.target as Element).closest("input, label, a, button, select, textarea, [role='button']")
  )
    return
  control()?.click()
}

onMounted(() => {
  if (props.autofocus) control()?.focus()
})
</script>

<template>
  <component
    :is="as"
    class="p-checkbox relative inline-flex items-start gap-2 select-none"
    :class="[
      $attrs.class,
      sizeClasses.text,
      indicator === 'hidden' && [
        'flex-col items-center! rounded-md text-center has-focus-visible:ring-2',
        appearance.ring,
        highlight && 'ring-1'
      ],
      {
        'flex-row-reverse': indicator === 'end',
        'w-full rounded-lg border transition-colors': variant === 'card',
        'cursor-not-allowed opacity-50': disabled,
        'cursor-pointer': !disabled
      },
      variant === 'card' && [
        sizeClasses.card,
        appearance.cardFocus,
        modelValue || indeterminate || highlight
          ? appearance.border
          : 'border-gray-lighten-1/25 dark:border-gray-lighten-1-dark/25',
        modelValue ? appearance.card : !disabled && appearance.hover
      ]
    ]"
    :style="$attrs.style as StyleValue"
    @click="onCardClick"
  >
    <span
      class="relative inline-flex shrink-0"
      :class="indicator === 'hidden' ? 'sr-only' : sizeClasses.control"
    >
      <UCheckbox
        :key="inputId"
        ref="checkbox"
        v-bind="inputAttrs()"
        :id="inputId"
        :model-value="indeterminate ? 'indeterminate' : modelValue"
        :disabled="disabled"
        :required="required"
        :name="name"
        :value="value"
        :size="size"
        :color="color"
        :indicator="indicator === 'hidden' ? 'hidden' : 'start'"
        :icon="indicator === 'hidden' ? undefined : CheckIcon"
        :indeterminate-icon="CheckIcon"
        :aria-describedby="describedBy()"
        :ui="{
          container: 'h-auto',
          base: normalizeClass([
            sizeClasses.control,
            'surface cursor-pointer border ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed',
            appearance.focus,
            modelValue || indeterminate || highlight
              ? appearance.border
              : 'border-gray-lighten-1 dark:border-gray-lighten-1-dark',
            highlight && ['ring-1', appearance.ring]
          ]),
          indicator: normalizeClass([appearance.checked, appearance.icon]),
          icon: 'size-3/4'
        }"
        @update:model-value="onUpdate"
        @keydown.enter.prevent="onEnter"
      />
    </span>
    <FontAwesomeIcon
      v-if="indicator === 'hidden' && icon"
      :icon="checkboxIcon"
      aria-hidden="true"
      class="shrink-0"
      :class="sizeClasses.control"
    />
    <span v-if="label || slots.label || hasDescription" class="min-w-0 leading-snug">
      <component
        :is="as === 'label' ? 'span' : 'label'"
        v-if="label || slots.label"
        :for="as === 'label' ? undefined : inputId"
        class="block font-medium"
        :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
      >
        <slot name="label" :label="label">{{ label }}</slot>
        <span v-if="required" class="text-error ml-0.5" aria-hidden="true">*</span>
      </component>
      <span v-if="hasDescription" :id="descriptionId" class="block opacity-70">
        <slot name="description" :description="description">{{ description }}</slot>
      </span>
    </span>
  </component>
</template>
